import React from 'react';
import { Link, Tooltip } from '@mui/material';
import { Translate, translate } from 'react-jhipster';
import moment from 'moment';
import { IBank } from 'app/shared/model/bank.model';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

export const CancelOrderButton = ({
                                    purchaseCancellation,
                                    quotationEntity,
                                    account,
                                    poCreatedDateData,
                                    getDateOnly,
                                    handleRequestCancellationClick,
                                    AUTHORITIES,
                                    bank,
                                  }: {
  purchaseCancellation: any;
  quotationEntity: any;
  account: any;
  poCreatedDateData: string;
  getDateOnly: (date: Date) => Date;
  handleRequestCancellationClick: () => void;
  AUTHORITIES: any;
  bank?: IBank;
}) => {
  // All roles that are authorized to perform cancellation
  const isAuthorized =
    account?.authorities?.includes(AUTHORITIES.ADMIN) ||
    account?.authorities?.includes(AUTHORITIES.ALSHMOKH_ADMIN) ||
    account?.authorities?.includes(AUTHORITIES.ALSHMOKH_USER) ||
    account?.authorities?.includes(AUTHORITIES.SUBADMIN_CANCEL_TRANSACTIONS) ||
    account?.cancellationPermission;

  // Only show anything for authorized users
  if (!isAuthorized) return <></>;

  // --- Handle existing cancellation request states ---
  if (purchaseCancellation?.id > 0) {
    if (purchaseCancellation?.comexAdminApproval === null || purchaseCancellation?.result === 'APPROVED') {
      return (
        <Link underline="none" color="error" className="cancelRequestBtn" style={{ display: 'none' }}>
          <Translate contentKey="comexApp.quotation.requestCancellationPendingApproval" />
        </Link>
      );
    }
    if (purchaseCancellation?.managerApproval !== null &&
      purchaseCancellation?.comexAdminApproval === true &&
      purchaseCancellation?.result === 'CANCELLED') {
      return (
        <Link underline="none" color="error" className="cancelRequestBtn" style={{ display: 'none' }}>
          <Translate contentKey="comexApp.quotation.cancellationApproved" />
        </Link>
      );
    }
    if (purchaseCancellation?.managerApproval !== null && purchaseCancellation?.comexAdminApproval === false) {
      return (
        <Link underline="none" color="error" className="cancelRequestBtn" style={{ display: 'none' }}>
          <Translate contentKey="comexApp.quotation.cancellationDenied" />
        </Link>
      );
    }
    return <></>;
  }

  // --- No existing cancellation — evaluate whether to show button or warning ---

  // Check if bank cancellation is fully configured
  const isBankConfigured =
    bank?.allowedStatusToCancel != null && bank.allowedStatusToCancel.trim() !== '' &&
    bank?.minDaysToCancel != null &&
    bank?.maxDaysToCancel != null;

  // Not configured → all authorized users see warning + disabled link
  if (!isBankConfigured) {
    return (
      <Tooltip title={translate('comexApp.bank.details.cancellationSetting.notConfiguredWarning')}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#ed6c02', fontSize: 13, cursor: 'default' }}>
          <WarningAmberIcon fontSize="small" />
          <Link
            underline="none"
            color="inherit"
            className="cancelRequestBtn"
            sx={{ pointerEvents: 'none', opacity: 0.6, fontSize: 'inherit' }}
          >
            <Translate contentKey="comexApp.quotation.requestCancellation" />
          </Link>
        </span>
      </Tooltip>
    );
  }

  // Bank is fully configured — evaluate status and day range
  const today = new Date();
  const poCreatedDate = moment(poCreatedDateData, 'YYYY/MM/DD HH:mm:ss').toDate();
  const normalizedPoCreatedDate = getDateOnly(poCreatedDate);
  const normalizedToday = getDateOnly(today);
  // +1 so that the creation day itself counts as day 1
  const elapsedDays = Math.floor(
    (normalizedToday.getTime() - normalizedPoCreatedDate.getTime()) / (1000 * 60 * 60 * 24)
  ) + 1;

  const currentStatus = quotationEntity?.status ?? '';
  const allowedStatuses = bank.allowedStatusToCancel
    .split(',')
    .map((s: string) => s.trim().toLowerCase());
  const isStatusAllowed = allowedStatuses.includes(currentStatus.toLowerCase());
  const isDayRangeAllowed =
    elapsedDays >= bank.minDaysToCancel &&
    elapsedDays <= bank.maxDaysToCancel;

  if (isStatusAllowed && isDayRangeAllowed) {
    return (
      <Link
        underline="hover"
        color="error"
        className="cancelRequestBtn"
        id="save-entity"
        data-cy="entityCreateSaveButton"
        onClick={handleRequestCancellationClick}
        sx={{ cursor: 'pointer' }}
      >
        <Translate contentKey="comexApp.quotation.requestCancellation" />
      </Link>
    );
  }

  return <></>;
};
